// Capacitor 네이티브 셸(android/ios) 전용 촉각 피드백. 없어도 앱은 온전히 도는
// 부가 기능이라, 초기 로딩 경로에 절대 끼지 않고 진동을 위해 사용자의 탭을 한
// 순간도 막지 않는다. 설계 원칙 세 가지:
//   1) @capacitor/haptics 는 별도 청크로 분리돼 앱 부팅과 무관하게 지연 다운로드된다
//      (vite.config 에서 external → apps/app 의 manualChunks 가 vendor-capacitor 로 분리).
//   2) 호출은 fire-and-forget — 청크 로드를 await 하지 않는다(반환 타입 void).
//   3) 청크가 아직 안 왔으면 그 호출은 조용히 버린다(no-op). 즉 "로드가 끝난
//      순간부터" 진동이 살아난다. 그전 몇 번의 탭이 진동을 놓치는 건 감수한다.
//
// 이 유틸을 @app/ui 에 둔 이유: 토스트(같은 패키지)와 apps/app 의 task 뮤테이션이
// 모두 한 진입점을 공유해야 하는데, @app/ui 는 apps/app(detectPlatform)을 역참조할
// 수 없다. 그래서 네이티브 판정은 shared 의 isCapacitorNative() 하나를 공유한다 —
// apps/app 의 detectPlatform() 과 같은 함수라 판정 로직이 갈라지지 않는다.

import { isCapacitorNative } from "shared/features/platform";

type HapticsModule = typeof import("@capacitor/haptics");

/** 로드 완료 후 캐시된 모듈. null 이면 아직 안 왔다는 뜻(호출은 no-op). */
let mod: HapticsModule | null = null;
/** 다운로드 중복 착수 방지(로드 실패 시 다시 false 로 돌려 재시도 허용). */
let started = false;

/**
 * 네이티브에서 딱 한 번, 백그라운드로 haptics 청크를 당겨온다. await 하지 않는다.
 * 첫 상호작용이 진동을 놓치는 창을 줄이려면 네이티브 부팅 시(PlatformBridge 의
 * requestIdleCallback 등) 미리 호출해 데워둘 수 있다 — 부팅 크리티컬 패스는 아니다.
 */
export function warmHaptics(): void {
  if (started || mod || !isCapacitorNative()) return;
  started = true;
  import("@capacitor/haptics")
    .then((loaded) => {
      mod = loaded;
    })
    .catch(() => {
      // 청크 로드 실패 등 — 다음 기회에 재시도 허용.
      started = false;
    });
}

/**
 * 실제 진동 발사. try/catch(동기 throw) + .catch(비동기 reject) 이중 방어가 핵심이다.
 * 웹만 배포하고 네이티브(스토어 빌드)는 아직 갱신 안 된 기기에서는, 원격 JS 에 담긴
 * haptics 청크는 정상 로드되지만 그 JS 가 부르는 네이티브 플러그인 브리지가 구버전
 * 셸에 없어 Capacitor 가 "not implemented" 로 던진다(동기 throw 또는 promise reject).
 * 그 에러를 여기서 삼켜 UX 를 깨뜨리지 않는다 — 그래서 이 과도기엔 조용히 no-op 이다.
 */
function fire(run: (m: HapticsModule) => Promise<void>): void {
  if (!mod) return;
  try {
    void run(mod).catch(() => {});
  } catch {
    // 브리지 미등록 시 동기 throw 하는 Capacitor 버전 대비.
  }
}

/** 툭 치는 물리적 임팩트. task 추가(Medium)·일반 status 변경(Light) 등에 쓴다. */
export function hapticImpact(style: "Light" | "Medium" | "Heavy"): void {
  warmHaptics(); // 아직이면 로드를 시작만 시킨다 — 이번 호출은 아래에서 no-op.
  fire((m) => m.Haptics.impact({ style: m.ImpactStyle[style] }));
}

/** 결과 통지. 완료·토스트 성공/경고/에러에 쓴다(impact 보다 뚜렷한 패턴). */
export function hapticNotification(type: "Success" | "Warning" | "Error"): void {
  warmHaptics();
  fire((m) => m.Haptics.notification({ type: m.NotificationType[type] }));
}
